import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { AdminLayout } from "./AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  FileText, Send, ChevronDown, ChevronRight, CheckCircle,
  Home, Info, Calendar, Users, Camera, Heart, Mail, Mic,
  Video, ShoppingCart, Ticket, Wallet, Settings, Radio,
  Globe, Headphones, LayoutDashboard, BookOpen, Printer, Download
} from "lucide-react";

const sitePages = [
  { id: "homepage", name: "Homepage", icon: Home, sections: [
    "Hero Banner & Logo", "Mission Statement", "Podcast Section", "Featured Events", "Impact Statistics",
    "Early Detection Video", "Mobile Screening Unit", "Gallery/Our Moments", "Make a Difference CTA",
    "Partners Scrolling Bar", "Sponsor Spotlight", "Donation CTA", "Community Highlights", "Newsletter Signup"
  ]},
  { id: "about", name: "About Page", icon: Info, sections: [
    "Hero Section", "Mission & Values", "Vision Statement", "History/Story", "Team Section", "Impact Stats"
  ]},
  { id: "events", name: "Events", icon: Calendar, sections: [
    "Events List", "Event Detail Page", "Ticket Types & Pricing", "Event Images/Media", "Event Location & Map", "Registration Form"
  ]},
  { id: "community", name: "Community Hub", icon: Users, sections: [
    "Community Feed", "Stories Bar", "Post Creation", "Comments & Likes", "Share Buttons", "Featured Posts"
  ]},
  { id: "podcast", name: "Podcast", icon: Headphones, sections: [
    "Podcast List", "Episode Details", "Live Podcast Studio", "Director Mode", "Commercial Breaks", "Placeholder Video", "Guest Management"
  ]},
  { id: "live", name: "Live Streams", icon: Radio, sections: [
    "Stream List", "Go Live Flow", "Viewer Page", "Director Controls", "Chat", "Recording/Playback"
  ]},
  { id: "conference", name: "Conference Room", icon: Video, sections: [
    "Meeting List", "Create Meeting", "Waiting Room", "Video Grid", "Chat", "Recording"
  ]},
  { id: "gallery", name: "Gallery", icon: Camera, sections: [
    "Photo Grid", "Featured Photos", "Categories", "Upload System"
  ]},
  { id: "campaigns", name: "Campaigns", icon: Heart, sections: [
    "Campaign List", "Campaign Detail", "Donation Form", "Progress Bar", "Campaign Images/Media"
  ]},
  { id: "blog", name: "Blog/News", icon: BookOpen, sections: [
    "Blog List", "Blog Detail", "Categories & Tags", "Featured Posts", "Images/Media"
  ]},
  { id: "getinvolved", name: "Get Involved", icon: Users, sections: [
    "Volunteer Form", "Sponsorship Levels", "Partner With Us", "Join Community", "Current Opportunities"
  ]},
  { id: "contact", name: "Contact", icon: Mail, sections: [
    "Contact Form", "Map", "Phone/Email/Address", "Social Links"
  ]},
  { id: "shopping", name: "Shopping Cart & Wallet", icon: ShoppingCart, sections: [
    "Cart Drawer", "Checkout Flow", "Payment Methods", "My Dashboard", "My Tickets", "Digital Wallet", "QR Codes"
  ]},
  { id: "admin", name: "Admin Dashboard", icon: LayoutDashboard, sections: [
    "Dashboard Overview", "Event Management", "Blog Management", "Donation Tracking", "Ticket Management",
    "Comp Tickets", "Supporter Management", "Gallery Management", "Partner Management", "Newsletter Management",
    "Weekly Digest", "Analytics", "Stream Management", "Live Permissions", "User Management", "Settings"
  ]},
  { id: "auth", name: "Authentication", icon: Globe, sections: [
    "Login Page", "Register Page", "Google Auth", "Password Reset", "User Profile"
  ]},
  { id: "navigation", name: "Navigation & Layout", icon: Settings, sections: [
    "Header/Navbar", "Mobile Menu", "Footer", "Logo", "Scroll to Top", "Sign In Button"
  ]},
];

export default function AdminSiteChanges() {
  const [expandedPage, setExpandedPage] = useState(null);
  const [changes, setChanges] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [priority, setPriority] = useState("normal");
  const [notes, setNotes] = useState("");

  const updateChange = (pageId, section, value) => {
    setChanges(prev => ({
      ...prev,
      [`${pageId}__${section}`]: value
    }));
  };

  const getChangeCount = () => Object.values(changes).filter(v => v?.trim()).length;

  const getChangeList = () => Object.entries(changes).filter(([, v]) => v?.trim()).map(([key, value]) => {
    const [pageId, ...sectionParts] = key.split("__");
    const page = sitePages.find(p => p.id === pageId);
    return { page: page?.name || pageId, section: sectionParts.join("__"), description: value };
  });

  const handlePrint = () => {
    const changeList = getChangeList();
    const printWin = window.open("", "_blank");
    printWin.document.write(`<html><head><title>Website Change Request</title><style>body{font-family:Arial,sans-serif;max-width:700px;margin:40px auto;color:#333}h1{color:#0a1e3d;font-size:22px}h2{color:#0a1e3d;font-size:16px;border-bottom:1px solid #ddd;padding-bottom:4px;margin-top:24px}p{font-size:13px;line-height:1.6}.meta{color:#666;font-size:12px}.change{background:#f8fafc;padding:12px;border-radius:6px;margin:8px 0;border-left:3px solid #0a1e3d}.priority{display:inline-block;padding:2px 8px;border-radius:4px;font-size:11px;font-weight:bold}img{height:60px;margin-bottom:12px}</style></head><body>`);
    printWin.document.write(`<img src="https://customer-assets.emergentagent.com/job_6cd70ab0-164b-4026-90ab-4bbce9b2006d/artifacts/29dc4973_BluevsBlue1%20%281%29.png" alt="Logo" />`);
    printWin.document.write(`<h1>Website Change Request</h1>`);
    printWin.document.write(`<p class="meta">Submitted by: ${contactName || "—"} (${contactEmail || "—"})<br/>Priority: <span class="priority" style="background:${priority==='urgent'?'#fecaca':priority==='high'?'#fed7aa':'#e2e8f0'}">${priority.toUpperCase()}</span><br/>Date: ${new Date().toLocaleDateString()}</p>`);
    if (notes) printWin.document.write(`<p><strong>Notes:</strong> ${notes}</p>`);
    printWin.document.write(`<h2>${changeList.length} Change(s) Requested</h2>`);
    changeList.forEach(c => { printWin.document.write(`<div class="change"><strong>${c.page} › ${c.section}</strong><p>${c.description}</p></div>`); });
    printWin.document.write(`<p class="meta" style="margin-top:24px;text-align:center">Blue vs Blue | Prostate Cancer Awareness Organization</p></body></html>`);
    printWin.document.close();
    printWin.print();
  };

  const handleDownload = () => {
    const changeList = getChangeList();
    let txt = "BLUE VS BLUE — WEBSITE CHANGE REQUEST\n";
    txt += "═".repeat(50) + "\n\n";
    txt += `Submitted by: ${contactName || "—"} (${contactEmail || "—"})\n`;
    txt += `Priority: ${priority.toUpperCase()}\n`;
    txt += `Date: ${new Date().toLocaleDateString()}\n`;
    txt += `Changes: ${changeList.length}\n`;
    if (notes) txt += `Notes: ${notes}\n`;
    txt += "\n" + "─".repeat(50) + "\n\n";
    changeList.forEach((c, i) => { txt += `${i+1}. [${c.page}] ${c.section}\n   ${c.description}\n\n`; });
    txt += "─".repeat(50) + "\n";
    txt += "Blue vs Blue | Early detection is key.\n";
    const blob = new Blob([txt], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `BlueVsBlue-ChangeRequest-${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (getChangeCount() === 0) { toast.error("Please describe at least one change"); return; }
    setSubmitting(true);
    try {
      const changeList = Object.entries(changes)
        .filter(([, v]) => v?.trim())
        .map(([key, value]) => {
          const [pageId, ...sectionParts] = key.split("__");
          const page = sitePages.find(p => p.id === pageId);
          return { page: page?.name || pageId, section: sectionParts.join("__"), description: value };
        });

      await axios.post(`${API}/admin/site-changes`, {
        submitted_by: contactName,
        email: contactEmail,
        priority,
        notes,
        changes: changeList,
        submitted_at: new Date().toISOString()
      }, { withCredentials: true });

      toast.success("Change request submitted!");
      setSubmitted(true);
    } catch { toast.error("Failed to submit"); }
    finally { setSubmitting(false); }
  };

  return (
    <AdminLayout currentPage="Site Changes">
      <div className="space-y-6" data-testid="admin-site-changes">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <img src="https://customer-assets.emergentagent.com/job_6cd70ab0-164b-4026-90ab-4bbce9b2006d/artifacts/29dc4973_BluevsBlue1%20%281%29.png" alt="Blue vs Blue" className="h-12 rounded-lg" />
            <div>
              <h1 className="text-2xl font-bold text-deep-blue">Website Change Request Form</h1>
              <p className="text-slate-500 mt-1">Submit professional change requests for any page or section</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={handlePrint}><Printer className="w-3 h-3 mr-1" /> Print</Button>
            <Button size="sm" variant="outline" onClick={handleDownload}><Download className="w-3 h-3 mr-1" /> Download</Button>
          </div>
        </div>

        {submitted ? (
          <div className="bg-white rounded-xl border border-green-200 p-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-deep-blue mb-2">Change Request Submitted</h2>
            <p className="text-slate-500 mb-4">{getChangeCount()} change(s) have been logged for review.</p>
            <Button onClick={() => { setSubmitted(false); setChanges({}); setNotes(""); }} variant="outline">Submit Another Request</Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Contact Info */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="font-bold text-deep-blue mb-4">Requester Information</h2>
              <div className="grid md:grid-cols-3 gap-4">
                <div><Label>Name *</Label><Input value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="Your name" required /></div>
                <div><Label>Email *</Label><Input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder="your@email.com" required /></div>
                <div><Label>Priority</Label>
                  <select value={priority} onChange={(e) => setPriority(e.target.value)} className="w-full border rounded-md px-3 py-2 text-sm">
                    <option value="low">Low — Nice to have</option>
                    <option value="normal">Normal — Standard update</option>
                    <option value="high">High — Needed soon</option>
                    <option value="urgent">Urgent — Critical fix</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Page Sections */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="font-bold text-deep-blue mb-2">Select Pages & Describe Changes</h2>
              <p className="text-sm text-slate-500 mb-4">Expand any page below and describe what changes you need for each section.</p>

              <div className="space-y-2">
                {sitePages.map(page => (
                  <div key={page.id} className="border border-slate-200 rounded-lg overflow-hidden">
                    <button type="button" onClick={() => setExpandedPage(expandedPage === page.id ? null : page.id)}
                      className="w-full flex items-center gap-3 p-3 hover:bg-slate-50 text-left">
                      {expandedPage === page.id ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                      <page.icon className="w-5 h-5 text-awareness-blue" />
                      <span className="font-semibold text-deep-blue flex-1">{page.name}</span>
                      {page.sections.some(s => changes[`${page.id}__${s}`]?.trim()) && (
                        <span className="bg-awareness-blue text-white text-[10px] px-2 py-0.5 rounded-full">
                          {page.sections.filter(s => changes[`${page.id}__${s}`]?.trim()).length} change(s)
                        </span>
                      )}
                    </button>
                    {expandedPage === page.id && (
                      <div className="border-t border-slate-200 p-4 bg-slate-50 space-y-4">
                        {page.sections.map(section => (
                          <div key={section}>
                            <Label className="text-sm font-medium text-deep-blue">{section}</Label>
                            <Textarea
                              value={changes[`${page.id}__${section}`] || ""}
                              onChange={(e) => updateChange(page.id, section, e.target.value)}
                              placeholder={`Describe changes needed for "${section}"...`}
                              rows={2}
                              className="mt-1 text-sm"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Additional Notes */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="font-bold text-deep-blue mb-4">Additional Notes</h2>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any additional context, links to reference designs, or general feedback..." rows={4} />
            </div>

            {/* Submit */}
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-500">{getChangeCount()} change(s) described</p>
              <Button type="submit" disabled={submitting || getChangeCount() === 0} className="bg-deep-blue text-white px-8">
                <Send className="w-4 h-4 mr-2" /> {submitting ? "Submitting..." : "Submit Change Request"}
              </Button>
            </div>
          </form>
        )}
      </div>
    </AdminLayout>
  );
}
